<template>
  <div class="sage-export-conflicts-dialog-container">
    <div class="sage-export-conflicts-body">
      <StaticAlertComponent :alert="errorAlert" />
      <p class="sage-export-conflicts-subtitle">{{ texts.subtitle }}</p>
      <p class="sage-export-conflicts-message">{{ texts.message }}</p>

      <SpinnerLayerComponent v-if="!sageConflictsGridColumns" />
      <SimpleGridTableComponent
        v-else
        :columns="sageConflictsGridColumns"
        :itemsData="conflicts"
        :paginationPageSize="PAGE_SIZE"
        :title="texts.gridTitle"
        :showGrid="true"
        :showButtons="false"
      />
    </div>

    <CustomDialogActionButtonsComponent
      :showSecondaryButton="false"
      :primaryButtonText="texts.primaryButtonText"
      :mainAction="closeDialog"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n-composable'
import store from '@/store/store'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { ListNames } from '@/store/modules/configuration/configurationTypes'
import CustomDialogActionButtonsComponent from '@/components/Dialog/CustomDialog/CustomDialogActionButtonsComponent.vue'
import SimpleGridTableComponent from '@/components/grids/SimpleGridTable/SimpleGridTableComponent.vue'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'
import StaticAlertComponent from '@/components/Alert/StaticAlertComponent.vue'
import { AlertsTypes } from '@/store/modules/alerts/alertsTypes'

const PAGE_SIZE = 5

const { t } = useI18n()

const sageConflictsGridColumns = ref<string>('')

const props = defineProps<{
  componentProps: { conflicts: any[] }
}>()

const conflicts = computed(() => {
  const base = process.env.VUE_APP_FRONT_URL?.replace(/\/$/, '') ?? ''
  return (props.componentProps?.conflicts ?? []).map((conflict: any) => ({
    ...conflict,
    link: conflict.link ? `${base}/${conflict.link}` : conflict.link
  }))
})

const texts = {
  title: t('components.expedient_economic_data.sage_export_conflicts_title').toString(),
  subtitle: t('components.expedient_economic_data.sage_export_conflicts_subtitle').toString(),
  message: t('components.expedient_economic_data.sage_export_conflicts_message').toString(),
  gridTitle: t('components.expedient_economic_data.sage_export_conflicts_grid_title').toString(),
  primaryButtonText: t('action_buttons.cancel_export').toString()
}

const errorAlert = { type: AlertsTypes.ERROR, message: texts.title }

const closeDialog = () => {
  store.dispatch(`${ModuleNamespaces.DIALOG}/closeCustomDialog`)
}

onMounted(async () => {
  const alias = ListNames.BANKDRAFT_ERRORS_SAGE
  await store.dispatch(`${ModuleNamespaces.CONFIGURATION}/fetchCurrentListConfigurationContext`, {
    alias,
    context: alias
  })
  const parsed = store.getters[`${ModuleNamespaces.CONFIGURATION}/getCurrentListConfigurationContext`](alias) ?? {
    columns: []
  }
  sageConflictsGridColumns.value = JSON.stringify(parsed.columns?.[0] ?? [])
})
</script>

<style scoped lang="scss">
.sage-export-conflicts-dialog-container {
  width: 100%;

  :deep(.static-alert-container) {
    .alert-info-container {
      font-weight: bold;
    }
  }

  :deep(.simple-grid) {
    .e-grid .e-gridheader {
      border-top: none !important;
      box-shadow: none !important;
    }

    .e-grid .e-headercell .e-rhandler {
      border: none !important;
      box-shadow: none !important;
    }

    .e-gridpager {
      display: flex;
      align-items: center;

      .e-pagesizes {
        display: none;
      }

      .e-pagercontainer {
        flex: 1;
      }

      .e-parentmsgbar {
        margin-left: auto;
        float: none;
      }
    }
  }
}

.sage-export-conflicts-body {
  margin-bottom: $spacing-lg;

  .sage-export-conflicts-subtitle {
    @include milano-bold-16;
    margin-bottom: $spacing-xs;
  }

  .sage-export-conflicts-message {
    @include milano-regular-14;
    margin-bottom: $spacing-md;
  }
}
</style>
